import { Link } from 'react-router-dom';
import type { Client } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, User, Mail, Clock } from 'lucide-react';

interface ClientCardProps {
  client: Client & {
    totalHours?: number;
    earnings?: number;
    activeTasks?: number;
  };
}

export function ClientCard({ client }: ClientCardProps) {
  const currency = client.currency || 'EUR';
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;

  return (
    <Link to={`/clients/${client.id}`}>
      <Card className="border border-border hover:border-primary/50 transition-colors">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                {client.type === 'business' ? (
                  <Building2 className="w-5 h-5 text-primary" />
                ) : (
                  <User className="w-5 h-5 text-primary" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{client.name}</h3>
                {client.businessName && (
                  <p className="text-sm text-muted-foreground">{client.businessName}</p>
                )}
              </div>
            </div>
            <Badge variant={client.type === 'business' ? 'default' : 'secondary'}>
              {client.type === 'business' ? 'Business' : 'Private'}
            </Badge>
          </div>

          {/* Info */}
          <div className="space-y-2 text-sm">
            {client.email && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span className="truncate">{client.email}</span>
              </div>
            )}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>{(client.totalHours || 0).toFixed(1)}h</span>
              </div>
              <div className="flex items-center gap-1.5 font-medium">
                <span>{symbol} {(client.earnings || 0).toLocaleString()}</span>
              </div>
              {client.activeTasks !== undefined && client.activeTasks > 0 && (
                <Badge variant="outline">{client.activeTasks} active</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

interface ClientsCardGridProps {
  clients: (Client & {
    totalHours?: number;
    earnings?: number;
    activeTasks?: number;
  })[];
  emptyState?: React.ReactNode;
}

export function ClientsCardGrid({ clients, emptyState }: ClientsCardGridProps) {
  if (clients.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {clients.map((client) => (
        <ClientCard key={client.id} client={client} />
      ))}
    </div>
  );
}
